/**
 * Match-time helpers.
 *
 * Maps Opta's `periodId` enum to the short labels shown in score widgets:
 *   1  → first half               → `${matchTime}'`
 *   2  → second half              → `${matchTime}'`
 *   3  → first half of extra time → `${matchTime}'`
 *   4  → second half of extra time → `${matchTime}'`
 *   5  → penalty shootout          → 'PK'
 *   10 → half-time                 → 'HT'
 *   11 → end of regulation         → 'ET'
 *   12 → end of first half ET      → 'ET'
 */

/** @typedef {import('./types').Match} Match */

/** Mapping from `periodId` to a fixed display label. */
const PERIOD_LABELS = /** @type {Record<number, string>} */ ({
  5: 'PK',
  10: 'HT',
  11: 'ET',
  12: 'ET',
});

/**
 * Short label representing the current point in the match, e.g. `"42'"`,
 * `"HT"`, `"ET"`, or `"PK"`. Returns an empty string when no useful info is
 * available (e.g. pre-match).
 *
 * @param {Match} match
 * @returns {string}
 */
export const getMatchTime = (match) => {
  const details = match?.liveData?.matchDetails;
  if (!details) return '';

  const period = Number(details.periodId);
  const fixedLabel = PERIOD_LABELS[period];
  if (fixedLabel) return fixedLabel;

  return details.matchTime ? `${details.matchTime}'` : '––';
};

/**
 * `true` when the match has progressed beyond regulation (i.e. extra time
 * or penalties). We use the number of `period` entries on the match details
 * payload as a proxy — Opta adds a new period each time play resumes.
 *
 * @param {Match} match
 * @returns {boolean}
 */
export const isOvertime = (match) =>
  (match?.liveData?.matchDetails?.period?.length ?? 0) > 2;
